/**
 * Pure keydown/double-click callbacks for `pinned-workspace` tree rows
 * (Task 17 — F14/G17). Extracted out of `TreeNodeContent` so this row-open
 * behavior is unit-testable without mounting the whole row, keeping that
 * hot component lean.
 *
 * Interaction contract (design doc §"Interaction contract (F14, G17)"):
 *  - single-click still selects (existing `onClick` on the row, unchanged);
 *  - double-click opens the pin — `stopPropagation` so it never reaches any
 *    ancestor expand-toggle handling (content-pin rows have no children);
 *  - `Enter` opens the pin, giving content-pin rows (which become focusable,
 *    `tabIndex={0}`, in `TreeNodeContent`) a real keyboard path to Open —
 *    wrapping a div in a context-menu trigger does not make it keyboard-
 *    reachable on its own (G17).
 *  - the OS/browser "ContextMenu" key already fires a native `contextmenu`
 *    DOM event on the focused element, which the row's existing
 *    `ContextMenuTrigger` wiring (`TreeNodeContextMenu`) already handles —
 *    no extra keydown branch is needed for that key once the row is
 *    focusable.
 */
import type React from 'react';
import type { PinnedWorkspaceRef } from '../../../../types/electron';
import { openWorkspacePin } from '../../../database-objects/services/pinnedObjects/openWorkspacePin';

export interface PinnedWorkspaceOpenArgs {
  connectionId: string;
  ref: PinnedWorkspaceRef;
}

/**
 * `Enter` opens the pin (mirrors double-click and the context menu's `Open`).
 * All other keys are left alone. Guards `target === currentTarget` so
 * tabbing PAST the row onto a focusable descendant (the sortable drag-handle
 * button rendered for pinned siblings) and pressing Enter there doesn't
 * double-fire Open via keydown bubbling — only Enter on the row itself opens.
 */
export function handlePinnedWorkspaceRowKeyDown(
  e: React.KeyboardEvent<HTMLElement>,
  args: PinnedWorkspaceOpenArgs,
): void {
  if (e.key !== 'Enter') return;
  if (e.target !== e.currentTarget) return;
  e.stopPropagation();
  void openWorkspacePin(args);
}

/** Double-click opens the pin. `stopPropagation` so it never toggles expansion. */
export function handlePinnedWorkspaceRowDoubleClick(
  e: React.MouseEvent<HTMLElement>,
  args: PinnedWorkspaceOpenArgs,
): void {
  e.stopPropagation();
  void openWorkspacePin(args);
}
